import { TitlesTypes, ItemTypes } from '../constants/constants';
import _ from 'lodash';
import { isATitleBox, isExcludedChapterTitle } from './boxes';
import {
  calculateHeight,
  calculateLeftPadding,
  calculateWidth,
} from '../constants/gridConfig';
import { sortVariableOrderArray } from './variableOrderArray';
import { mutateDeleteColumns } from './columns';

// CONSTANTS
// =============================================================================
export const headerIndentation = {
  [ItemTypes.TITLE]: 1,
  [ItemTypes.TITLE_2]: 2,
  [ItemTypes.TITLE_3]: 3,
  [ItemTypes.TITLE_4]: 4,
};
export const letterMaj = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  'Y',
  'Z',
];
export const letterMin = [
  'a',
  'b',
  'c',
  'd',
  'e',
  'f',
  'g',
  'h',
  'i',
  'j',
  'k',
  'l',
  'm',
  'n',
  'o',
  'p',
  'q',
  'r',
  's',
  't',
  'u',
  'v',
  'w',
  'x',
  'y',
  'z',
];
export const letterBlank = [''];
export const romanNumber = [
  '',
  'C',
  'CC',
  'CCC',
  'CD',
  'D',
  'DC',
  'DCC',
  'DCCC',
  'CM',
  '',
  'X',
  'XX',
  'XXX',
  'XL',
  'L',
  'LX',
  'LXX',
  'LXXX',
  'XC',
  '',
  'I',
  'II',
  'III',
  'IV',
  'V',
  'VI',
  'VII',
  'VIII',
  'IX',
];
// =============================================================================

// FUNCTIONS
// =============================================================================

export const romanize = (num) => {
  if (isNaN(num)) return NaN;
  var digits = String(+num).split(''),
    roman = '',
    i = 3;
  while (i--) roman = (romanNumber[+digits.pop() + i * 10] || '') + roman;
  return Array(+digits.join('') + 1).join('M') + roman;
};

export const getTitlesConfig = (config) => {
  const titlesConfig = _.pick(config, Object.values(TitlesTypes));
  const titlesDisplayTypes = Object.values(TitlesTypes).map((t) => {
    return titlesConfig[t].typeTitle;
  });
  return titlesDisplayTypes;
};

// for data sync when indexTitle is still string like "1.2.1"
export const createIndexArray = (indexTitleStr) => {
  return indexTitleStr.match(/\d+/g);
};

/**
 * Nombre de niveaux de titre masqués en partant du haut : 0 = tout est indexé,
 * 2 = les niveaux 1 et 2 n'ont pas de numéro et l'indexation commence au 3.
 *
 * Ancien réglage, remplacé par une case par niveau (cf. numberedTitleLevels) :
 * il ne sert plus qu'à lire les documents enregistrés avant. Les plus anciens
 * stockent même un booléen (masquage du seul niveau 1) : `true` vaut 1.
 */
export const hiddenTitleLevels = (hideTitleIndex) => {
  if (hideTitleIndex === true) return 1;
  if (!hideTitleIndex) return 0;
  return Math.max(0, Math.min(Number(hideTitleIndex) || 0, 4));
};

export const TITLE_LEVELS_COUNT = Object.keys(TitlesTypes).length;

/**
 * Niveaux numérotés, un booléen par niveau (indice 0 = niveau 1).
 *
 * Accepte les trois formes qu'a prises le réglage : un tableau de cases, ou
 * l'ancien « masqué à partir du niveau N » (nombre, ou booléen plus ancien
 * encore), qu'on traduit en cases pour ne rien avoir à migrer en base.
 */
export const titleLevelsFrom = (value) => {
  if (Array.isArray(value)) {
    return _.range(TITLE_LEVELS_COUNT).map((level) => value[level] !== false);
  }
  const hiddenLevels = hiddenTitleLevels(value);
  return _.range(TITLE_LEVELS_COUNT).map((level) => level >= hiddenLevels);
};

export const numberedTitleLevels = (configuration) =>
  titleLevelsFrom(
    configuration?.numberedTitleLevels ?? configuration?.hideTitleIndex
  );

/**
 * Numéros à afficher pour un titre, vides quand il est hors numérotation
 * (cf. mutateTitlesIndexes). `indexTitle` reste l'index d'identité — celui qui
 * situe le titre dans l'arbre et qui sert d'indentation — et ne convient donc
 * pas à l'affichage : un titre hors numérotation en a un lui aussi.
 *
 * Les documents d'avant l'option n'ont pas le champ : tout y est numéroté.
 */
export const titleDisplayIndex = (content) =>
  content?.displayIndexTitle ?? content?.indexTitle ?? [];

/**
 * Premier niveau numéroté : les niveaux sans numéro qui le précèdent sortent
 * aussi de la chaîne des sous-titres (« à partir du niveau 2 » affiche « 1. »
 * au niveau 2, pas « 1.1. »).
 *
 * Un niveau sans numéro plus bas, lui, garde son compteur dans la chaîne :
 * l'enlever ferait porter le même numéro à deux titres différents (1.1.1 et
 * 1.2.1 donneraient tous les deux « 1.1 »).
 */
const firstNumberedLevel = (levels) => {
  const first = levels.indexOf(true);
  return first === -1 ? levels.length : first;
};

export const displayTitleIndex = (
  indexTitleArray,
  separator,
  titlesTypeConfig,
  // Cases des niveaux numérotés, ou l'ancien réglage (cf. titleLevelsFrom).
  numberedLevels,
  // Séparateur après le dernier numéro ("1.2." ou "1.2"). Absent des documents
  // antérieurs à l'option : seul un `false` explicite le retire.
  finalSeparator = true
) => {
  const levels = titleLevelsFrom(numberedLevels);
  // Le niveau du titre lui-même : sans numéro, il n'affiche rien, même si ses
  // sous-titres en ont un.
  if (!levels[indexTitleArray.length - 1]) return '';
  const fromLevel = firstNumberedLevel(levels);
  const indexesToDisplay = transcriptTitle(
    indexTitleArray,
    titlesTypeConfig
  ).filter((_i, level) => level >= fromLevel);
  if (indexesToDisplay.length > 0) {
    return indexesToDisplay.join(separator) + (finalSeparator === false ? '' : separator);
  } else {
    return '';
  }
};

export const transcriptTitle = (indexCounter, titlesTypeConfig) => {
  return indexCounter.map((counter, index) => {
    const titleTypeConfig = titlesTypeConfig[index];
    if (titleTypeConfig === 'letterMin') {
      return letterMin[counter - 1];
    } else if (titleTypeConfig === 'letterMaj') {
      return letterMaj[counter - 1];
    } else if (titleTypeConfig === 'romain') {
      return romanize(counter);
    } else if (titleTypeConfig === 'letterBlank') {
      return letterMaj[0];
    } else if (titleTypeConfig === 'chiffreZero') {
      // "01", "06", "12" : zéro de tête sous 10 ; "100" reste "100".
      return String(counter).padStart(2, '0');
    } else if (titleTypeConfig === 'chiffreDoubleZero') {
      // "001", "012", "100" : zéros de tête sous 100.
      return String(counter).padStart(3, '0');
    } else {
      return counter;
    }
  });
};

/**
 * Normalise un indexTitle qui peut être :
 * - déjà un tableau de nombres (nouveau format)
 * - une string type "1.10.2" (ancien format)
 * - ou toute autre valeur bizarre → on renvoie []
 */
const normalizeIndexTitle = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const raw = createIndexArray(value) || [];
    return raw.map((v) => Number(v));
  }
  return [];
};

/**
 * Compare deux indexTitle élément par élément.
 * Retourne un nombre négatif si a < b, positif si a > b, 0 si égaux.
 * Ex: [1, 2] < [1, 10] < [2, 1]
 *
 * Tolère aussi l'ancien format string ("1.10.2").
 */
const compareIndexTitles = (a, b) => {
  const arrA = normalizeIndexTitle(a);
  const arrB = normalizeIndexTitle(b);
  const len = Math.max(arrA.length, arrB.length);
  for (let i = 0; i < len; i++) {
    const diff = (arrA[i] || 0) - (arrB[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
};

const getTitleLevelIndex = (boxType) => {
  // starts at 0 for "TITLE" ("title1")
  return Number(boxType[boxType.length - 1]) - 1;
};

const sortArrayOrderVariable = (draft, boxesToMove) => {
  if (
    boxesToMove.some(
      (box) =>
        box.type === ItemTypes.TEXT_VARIABLE ||
        box.type === ItemTypes.CHECKBOXS_VARIABLE ||
        box.type === ItemTypes.CUSTOM_TEXT_VARIABLE ||
        box.type === ItemTypes.VARIABLE_GROUP ||
        box.type === ItemTypes.SECTION_VARIABLE ||
        box.type === ItemTypes.SELECTOR_VARIABLE ||
        box.type === ItemTypes.IMG_VARIABLE ||
        box.type === ItemTypes.TABLE_VARIABLE ||
        box.type === ItemTypes.FILE_PDF_VARIABLE
    )
  ) {
    sortVariableOrderArray(draft);
  }
};

const allBoxesOutsideDrawContainer = (
  draft,
  {
    endContainerIndexEnd,
    drawModeLogicLength,
    startContainerIndex,
    sectionMovedLogic,
  }
) => {
  const boxes = draft.boxes;
  const containersArray = draft.containers.slice(
    startContainerIndex + drawModeLogicLength,
    endContainerIndexEnd - sectionMovedLogic
  );
  const allColumnIdsToDelete = containersArray.flatMap(
    (container) => container.columnsIds
  );
  return boxes.filter((box) => allColumnIdsToDelete.includes(box.columnId));
};

const findLastTitleinDrawContainer = (draft, { oldDragTitle, titleType }) => {
  return draft.boxes.find((box) => {
    if (
      isATitleBox(box.type) &&
      compareIndexTitles(
        box.content.indexTitle,
        oldDragTitle.content.indexTitle
      ) > 0
    ) {
      if (titleType[box.type] > titleType[oldDragTitle.type]) return true;
      // if(dropPosition === 0) return true
      else return box.type === oldDragTitle.type;
    } else return false;
  });
};

const moveBoxesInSameDrawContainer = (
  draft,
  {
    oldDragTitle,
    aboveDropTitle,
    dropPosition,
    boxesInColumn,
    startContainerIndex,
    titleType,
    sectionMovedLogic,
  }
) => {
  const boxes = draft.boxes;
  const containers = [...draft.containers];

  const initBoxesLength = boxes.length;
  const initContainersLength = draft.containers.length;
  const firstTitleBoxIndex = boxes.findIndex((box) => isATitleBox(box.type));
  const firstTitleBox = boxes.find((box) => isATitleBox(box.type));
  const firstContainerWithTitleInsideIndex = draft.containers.findIndex(
    (container) =>
      container.columnsIds.includes(firstTitleBox.columnId.toString())
  );

  const firstBoxId = boxesInColumn[0].id;
  const lastBoxId = boxesInColumn[boxesInColumn.length - 1].id;
  const containerRef = containers.find((container) =>
    container.columnsIds.includes(boxesInColumn[0].columnId)
  );

  const firstBoxIndex = boxes.findIndex((box) => box.id === firstBoxId);
  const lastBoxIndex = boxes.findIndex((box) => box.id === lastBoxId);
  const containerIndex = containers.findIndex(
    (container) => container.id === containerRef.id
  );

  const findNextTitleBox = function (draft, params) {
    var lastTitleInDraw = findLastTitleinDrawContainer(draft, params);

    if (!lastTitleInDraw) return undefined;

    if (
      lastTitleInDraw.columnId.toString() !==
      params.oldDragTitle.columnId.toString()
    ) {
      return lastTitleInDraw;
    }

    return findNextTitleBox(draft, {
      oldDragTitle: lastTitleInDraw,
      titleType: params.titleType,
    });
  };

  const calculateEndContainerIndex = function (
    draft,
    nextTitleBox,
    startContainerIndex
  ) {
    var containerIndex = draft.containers.findIndex(function (container) {
      return container.columnsIds.includes(
        nextTitleBox ? nextTitleBox.columnId : ''
      );
    });

    var endIndex =
      containerIndex === -1 ? draft.containers.length : containerIndex;

    return endIndex === startContainerIndex + 1
      ? startContainerIndex
      : endIndex;
  };

  // Calculate the end container index and drawModeLogicLength
  var nextTitleBox = findNextTitleBox(draft, {
    oldDragTitle: oldDragTitle,
    titleType: titleType,
  });
  var endContainerIndexEnd = calculateEndContainerIndex(
    draft,
    nextTitleBox,
    startContainerIndex
  );
  let drawModeLogicLength = 0;
  if (draft.columns.find((c) => c.id === oldDragTitle.columnId).drawMode) {
    if (endContainerIndexEnd - startContainerIndex - 1 > 0) {
      //If there is no more boxes in the container we delete the container
      const checkIfBoxInsideContainer = draft.boxes.find(
        (box) => box.columnId === oldDragTitle.columnId
      );
      if (checkIfBoxInsideContainer) {
        drawModeLogicLength = 1;
      } else {
        drawModeLogicLength = 0;
      }
    } else if (endContainerIndexEnd - startContainerIndex === 0) {
      drawModeLogicLength = 0;
    }
  }

  // Get boxes under the draw container ref
  const boxesUnderTitle = allBoxesOutsideDrawContainer(draft, {
    endContainerIndexEnd,
    drawModeLogicLength,
    startContainerIndex,
    sectionMovedLogic,
  });

  // Récupérer les containers à déplacer
  const containersUnderTitle = [];
  for (
    let i = startContainerIndex + drawModeLogicLength;
    i < endContainerIndexEnd - sectionMovedLogic;
    i++
  ) {
    containersUnderTitle.push(draft.containers[i]);
  }
  // Delete boxes and containers from their old position after the draw container ref
  const containersArrayToDelete = draft.containers.slice(
    startContainerIndex + drawModeLogicLength,
    endContainerIndexEnd - sectionMovedLogic
  );
  const allColumnIdsToDelete = containersArrayToDelete.flatMap(
    (container) => container.columnsIds
  );
  draft.boxes = draft.boxes.filter(
    (box) => !allColumnIdsToDelete.includes(box.columnId)
  );
  draft.containers.splice(
    startContainerIndex + drawModeLogicLength,
    endContainerIndexEnd -
      startContainerIndex -
      (drawModeLogicLength + sectionMovedLogic)
  );

  // delete boxes and containers from their old position
  draft.boxes.splice(firstBoxIndex, lastBoxIndex - firstBoxIndex + 1);
  draft.containers.splice(containerIndex, 1);

  //Delete hidden box and container linked section
  if (sectionMovedLogic === 1) {
    draft.boxes.splice(firstBoxIndex - sectionMovedLogic, 1);
    draft.containers.splice(containerIndex - sectionMovedLogic, 1);
  }

  //-----------------------------------------------------------------------------------
  // find the new position
  const nextBoxSameLevel = boxes.find((box) => {
    if (
      isATitleBox(box.type) &&
      compareIndexTitles(
        box.content.indexTitle,
        aboveDropTitle.node.indexTitle
      ) > 0
    ) {
      if (dropPosition === 0) return true;
      else return box.type === aboveDropTitle.node.type;
    } else return false;
  });

  const newContainerInsertIndex = draft.containers.findIndex((container) =>
    container.columnsIds.includes(nextBoxSameLevel?.columnId)
  );

  const nextBoxIndex = draft.boxes.findIndex((box) => {
    if (
      isATitleBox(box.type) &&
      compareIndexTitles(
        box.content.indexTitle,
        aboveDropTitle.node.indexTitle
      ) > 0
    ) {
      if (dropPosition === 0) return true;
      else return box.type === aboveDropTitle.node.type;
    } else return false;
  });

  const nextBoxIndexEnd = nextBoxIndex === -1 ? initBoxesLength : nextBoxIndex;
  const nextContainerIndex =
    newContainerInsertIndex === -1
      ? initContainersLength
      : newContainerInsertIndex;

  /* Logic if a linked section is moved */
  let linkedSectionBox = null;
  let linkedSectionContainer = null;
  if (sectionMovedLogic === 1) {
    // get the linked section box just before nextBoxIndexEnd
    linkedSectionBox = boxes[firstBoxIndex - sectionMovedLogic];
    // get the linked section container just before nextContainerIndex
    linkedSectionContainer = containers[containerIndex - sectionMovedLogic];
  }

  /* Boxes and container to inject to the new position */
  const boxesToMove = [
    ...(linkedSectionBox ? [linkedSectionBox] : []),
    ...boxesInColumn,
    ...boxesUnderTitle,
  ];

  const containersToMove = [
    ...(linkedSectionContainer ? [linkedSectionContainer] : []),
    containerRef,
    ...containersUnderTitle,
  ];

  /* Insert boxes and containers to the new position */
  draft.containers.splice(
    dropPosition === -1
      ? firstContainerWithTitleInsideIndex
      : nextContainerIndex,
    0,
    ...containersToMove
  );
  draft.boxes.splice(
    dropPosition === -1 ? firstTitleBoxIndex : nextBoxIndexEnd,
    0,
    ...boxesToMove
  );

  sortArrayOrderVariable(draft, boxesInColumn);
};

const nextBoxIndex = ({
  boxes,
  titleNewBox,
  dropPosition,
  initBoxesLength,
  forDelete = false,
  isLastChildren = false,
}) => {
  if (forDelete) return initBoxesLength;
  const nextBoxIndex = boxes.findIndex((box) => {
    if (
      isATitleBox(box.type) &&
      compareIndexTitles(
        box.content.indexTitle,
        titleNewBox.content.indexTitle
      ) > 0
    ) {
      if (dropPosition === 0) return true;
      if (isLastChildren) return true;
      else return box.type === titleNewBox.type;
    } else return false;
  });

  const nextBoxIndexEnd = nextBoxIndex === -1 ? initBoxesLength : nextBoxIndex;
  return nextBoxIndexEnd;
};

const deleteBoxesByPositionUnderTitleDelete = ({
  boxes,
  columnId,
  titleBox,
  draft,
}) => {
  // Ensure we have a valid title box to compare positions
  if (!titleBox || !isATitleBox(titleBox.type)) return boxes;

  // Filter boxes to exclude those under the title box in the same column
  let numberOfBoxesDeleted = 0;
  const boxesUnderTitleToDelete = boxes.filter(
    (b, idx) =>
      b.columnId === columnId &&
      b.top >= titleBox.top &&
      compareIndexTitles(b?.content?.indexTitle, titleBox.content.indexTitle) <
        0 &&
      b.id !== titleBox.id
  );
  const boxesUnderTitleToDeleteIds = boxesUnderTitleToDelete.map((b) => b.id);
  draft.boxes = draft.boxes.filter(
    (b) => !boxesUnderTitleToDeleteIds.includes(b.id) && b.id !== titleBox.id
  );
  return numberOfBoxesDeleted;
};

export const mutateMoveBoxesIndex = (
  draft,
  {
    oldDragTitle,
    aboveDropTitle,
    dropPosition,
    boxesInColumn,
    forDelete = false,
  }
) => {
  if (
    oldDragTitle?.columnId &&
    oldDragTitle.columnId === aboveDropTitle?.node?.columnId
  ) {
    const column = draft.columns.find((c) => c.id === oldDragTitle.columnId);
    if (column?.drawMode) {
      return;
    }
  }

  // if(oldDragTitle?.from_linked_section) {
  //   // debugger;
  //   return "Déplacement impossible dans une section liée";
  // }

  const boxes = draft.boxes;
  const boxesToMove = [];
  const containersToMove = [];
  const initBoxesLength = boxes.length;

  const firstTitleBoxIndex = boxes.findIndex((box) => isATitleBox(box.type));
  const firstTitleBox = boxes.find((box) => isATitleBox(box.type));

  const firstContainerWithTitleInsideIndex = draft.containers.findIndex(
    (container) =>
      container.columnsIds.includes(firstTitleBox.columnId.toString())
  );

  const isLastChildren = aboveDropTitle?.node?.isLast;

  // Trouver la nouvelle box et son container associé
  const titleNewBox = boxes.find((box) => box.id === aboveDropTitle?.node?.id);

  const nextBoxIndexEnd = nextBoxIndex({
    boxes,
    titleNewBox,
    dropPosition,
    initBoxesLength,
    forDelete: forDelete,
  });

  // Avoid inserting a title inside linked section or inside a draw container
  if (!forDelete) {
    if (
      boxes[nextBoxIndexEnd - 1]?.from_linked_section &&
      boxes[nextBoxIndexEnd]?.from_linked_section &&
      boxes[nextBoxIndexEnd - 1]?.fromSectionId ===
        boxes[nextBoxIndexEnd]?.fromSectionId
    ) {
      return 'Déplacement impossible dans une section liée';
    }

    const col1 = draft.columns.find(
      (c) => c.id === boxes[nextBoxIndexEnd]?.columnId
    );
    let previousTitle = null;
    for (let i = nextBoxIndexEnd - 1; i >= 0; i--) {
      const currentBox = boxes[i];
      if (isATitleBox(currentBox.type)) {
        previousTitle = currentBox;
        break; // Arrête dès qu'un titre est trouvé
      }
    }
    const col2 = draft.columns.find((c) => c.id === previousTitle?.columnId);
    if (col1?.id === col2?.id && col1.drawMode) {
      return 'Déplacement impossible dans une ligne de dessin';
    }
  }

  // Trouver le container du TitleBox
  let startContainerIndex = draft.containers.findIndex((container) =>
    container.columnsIds.includes(oldDragTitle.columnId)
  );

  // If the dragged title's container has a linkedContainerId, move both containers together
  let chapterSepBoxesToPrepend = [];
  const linkedTitleContainer = draft.containers[startContainerIndex];
  if (linkedTitleContainer?.linkedContainerId) {
    const linkedIdx = draft.containers.findIndex((c) => c.id === linkedTitleContainer.linkedContainerId);
    if (linkedIdx !== -1 && linkedIdx < startContainerIndex) {
      // The linked container (mask) precedes the linked title container — move from it
      startContainerIndex = linkedIdx;
      const colIds = new Set(draft.containers[linkedIdx].columnsIds);
      chapterSepBoxesToPrepend = draft.boxes.filter((b) => colIds.has(b.columnId));
    }
  }

  const titleType = {
    [ItemTypes.TITLE]: 4,
    [ItemTypes.TITLE_2]: 3,
    [ItemTypes.TITLE_3]: 2,
    [ItemTypes.TITLE_4]: 1,
  };

  // Trouver le prochain titre de même type, ou la fin du tableau si aucun n'est trouvé
  const nextTitleBox = draft.boxes.find((box) => {
    if (
      isATitleBox(box.type) &&
      compareIndexTitles(
        box.content.indexTitle,
        oldDragTitle.content.indexTitle
      ) > 0
    ) {
      if (titleType[box.type] > titleType[oldDragTitle.type]) return true;
      // if(dropPosition === 0) return true
      else return box.type === oldDragTitle.type;
    } else return false;
  });

  const endContainerIndex = draft.containers.findIndex((container) =>
    container.columnsIds.includes(nextTitleBox?.columnId)
  );

  const endContainerIndexEnd =
    endContainerIndex === -1 ? draft.containers.length : endContainerIndex;

  // Indices des boxes à déplacer
  const startBoxIndex = boxes.findIndex((box) => box.id === oldDragTitle.id);
  const endBoxIndex = draft.boxes.findIndex((box) => {
    if (
      isATitleBox(box.type) &&
      compareIndexTitles(
        box.content.indexTitle,
        oldDragTitle.content.indexTitle
      ) > 0
    ) {
      if (titleType[box.type] > titleType[oldDragTitle.type]) return true;
      // if(dropPosition === 0) return true
      else return box.type === oldDragTitle.type;
    } else return false;
  });

  const moveEndIndex = endBoxIndex === -1 ? boxes.length : endBoxIndex; // Si aucun titre n'est trouvé, déplacer jusqu'à la fin

  // Check if section linked is breaked
  if (
    boxes[moveEndIndex - 1]?.from_linked_section &&
    boxes[moveEndIndex]?.from_linked_section &&
    boxes[moveEndIndex - 1]?.fromSectionId ===
      boxes[moveEndIndex]?.fromSectionId
  ) {
    return 'Section liée brisée par le déplacement';
  }

  let sectionMovedLogic = 0;
  // Check if the content moved is a section
  if (boxes[startBoxIndex]?.from_linked_section) {
    sectionMovedLogic = 1;
  }
  // Récupérer les boxes à déplacer
  for (
    let i = startBoxIndex - sectionMovedLogic;
    i < moveEndIndex - sectionMovedLogic;
    i++
  ) {
    boxesToMove.push(boxes[i]);
  }
  // Prepend chapter sep mask boxes (collected separately, not contiguous with startBoxIndex)
  if (chapterSepBoxesToPrepend.length > 0) {
    boxesToMove.unshift(...chapterSepBoxesToPrepend);
  }

  // Récupérer les containers à déplacer
  for (
    let i = startContainerIndex - sectionMovedLogic;
    i < endContainerIndexEnd - sectionMovedLogic;
    i++
  ) {
    containersToMove.push(draft.containers[i]);
  }

  let drawModeLogicLength = 0;
  //if forDelete in title in drawMode container we delete all boxes under the title and not the title
  if (
    forDelete &&
    draft.columns.find((c) => c.id === oldDragTitle.columnId).drawMode
  ) {
    deleteBoxesByPositionUnderTitleDelete({
      boxes,
      columnId: oldDragTitle.columnId,
      titleBox: oldDragTitle,
      draft,
    });
    if (endContainerIndexEnd - startContainerIndex - 1 > 0) {
      //If there is no more boxes in the container we delete the container
      const checkIfBoxInsideContainer = draft.boxes.find(
        (box) => box.columnId === oldDragTitle.columnId
      );
      if (checkIfBoxInsideContainer) {
        drawModeLogicLength = 1;
      } else {
        drawModeLogicLength = 0;
      }
    } else if (endContainerIndexEnd - startContainerIndex === 0) {
      return;
    }
  }

  // If the title is in a draw container
  if (boxesInColumn?.length > 0 && !forDelete) {
    moveBoxesInSameDrawContainer(draft, {
      oldDragTitle,
      aboveDropTitle,
      dropPosition,
      boxesInColumn,
      startContainerIndex,
      titleType,
      sectionMovedLogic,
    });
    return;
  }

  // Si aucune box n'est déplacée
  if (
    dropPosition !== -1 &&
    nextBoxIndex({
      boxes: draft.boxes,
      titleNewBox,
      dropPosition,
      initBoxesLength,
      forDelete: forDelete,
      isLastChildren,
    }) === startBoxIndex
  ) {
    return false;
  }

  // Retirer les containers et boxes à l'ancienne position
  const containersArrayToDelete = draft.containers.slice(
    startContainerIndex + drawModeLogicLength - sectionMovedLogic,
    endContainerIndexEnd - sectionMovedLogic
  );
  const allColumnIdsToDelete = containersArrayToDelete.flatMap(
    (container) => container.columnsIds
  );
  draft.boxes = draft.boxes.filter(
    (box) => !allColumnIdsToDelete.includes(box.columnId)
  );
  draft.containers.splice(
    startContainerIndex + drawModeLogicLength - sectionMovedLogic,
    endContainerIndexEnd - (startContainerIndex + drawModeLogicLength)
  );

  // Stop here if is for delete a title and his content
  if (forDelete) {
    // Les containers retirés emportent leurs colonnes (et lignes imbriquées) :
    // sans ça elles restaient dans draft.columns, référencées par personne.
    mutateDeleteColumns(draft, allColumnIdsToDelete);
    return;
  }

  //-----------------ADD BOXES AND CONTANIER IN THEIR NEW POSITIONS------------------

  const nextBoxSameLevel = draft.boxes.find((box) => {
    if (
      isATitleBox(box.type) &&
      compareIndexTitles(
        box.content.indexTitle,
        titleNewBox.content.indexTitle
      ) > 0
    ) {
      if (dropPosition === 0) return true;
      if (isLastChildren) return true;
      else return box.type === titleNewBox.type;
    } else return false;
  });

  const newContainerInsertIndex = draft.containers.findIndex((container) =>
    container.columnsIds.includes(nextBoxSameLevel?.columnId)
  );

  const nextContainerIndex =
    newContainerInsertIndex === -1
      ? draft.containers.length
      : newContainerInsertIndex;

  let indexBoxToInsert = nextBoxIndex({
    boxes: draft.boxes,
    titleNewBox,
    dropPosition,
    initBoxesLength,
    forDelete: forDelete,
    isLastChildren,
  });

  const includeBoxLinkedSection =
    dropPosition === -1
      ? firstTitleBox?.from_linked_section
        ? 1
        : 0
      : nextBoxSameLevel?.from_linked_section
        ? 1
        : 0;

  draft.containers.splice(
    (dropPosition === -1
      ? firstContainerWithTitleInsideIndex
      : nextContainerIndex) - includeBoxLinkedSection,
    0,
    ...containersToMove
  );

  draft.boxes.splice(
    (dropPosition === -1 ? firstTitleBoxIndex : indexBoxToInsert) -
      includeBoxLinkedSection,
    0,
    ...boxesToMove
  );

  sortArrayOrderVariable(draft, boxesToMove);
};

export const selectLevelTitle = (
  titleId,
  {
    defaultConfiguration,
    selectedBox,
    boxes,
    boxId,
    updateBox,
    batchUpdateBox,
    updateIndexTitle,
  }
) => {
  const titleIndices = {
    [ItemTypes.TITLE]: 0,
    [ItemTypes.TITLE_2]: 1,
    [ItemTypes.TITLE_3]: 2,
    [ItemTypes.TITLE_4]: 3,
  };
  const titleType = {
    0: ItemTypes.TITLE,
    1: ItemTypes.TITLE_2,
    2: ItemTypes.TITLE_3,
    3: ItemTypes.TITLE_4,
  };
  let indexPositionTitle = titleIndices[titleId] ?? -1;

  const width = calculateWidth(
    defaultConfiguration.margins,
    defaultConfiguration.indentationTitles,
    indexPositionTitle
  );
  const left = calculateLeftPadding(
    defaultConfiguration.margins,
    defaultConfiguration.indentationTitles,
    indexPositionTitle
  );
  const valueIncrementation =
    indexPositionTitle - titleIndices[selectedBox.type];

  // Collect all updates to apply in a single store write when batchUpdateBox is available
  const batchUpdates = [];

  boxes.forEach((boxChild) => {
    if (!isATitleBox(boxChild.type)) return;
    if (boxId === boxChild.id) return;

    const content = boxChild.content.indexTitle.slice(
      0,
      selectedBox.content.indexTitle.length
    );

    if (selectedBox.content.indexTitle.join(',') === content.join(',')) {
      const newLevelType =
        titleType?.[titleIndices[boxChild.type] + valueIncrementation] ||
        ItemTypes.TITLE_4;
      const titleLevelIndexFactor =
        titleIndices[
          titleType?.[titleIndices[boxChild.type] + valueIncrementation] ||
            ItemTypes.TITLE_4
        ];
      const widthChildTitle = calculateWidth(
        defaultConfiguration.margins,
        defaultConfiguration.indentationTitles,
        titleLevelIndexFactor
      );
      const leftChildTittle = calculateLeftPadding(
        defaultConfiguration.margins,
        defaultConfiguration.indentationTitles,
        titleLevelIndexFactor
      );
      const fn = (box) => {
        box.type = newLevelType;
        box.clientWidth = widthChildTitle;
        box.clientHeight = calculateHeight();
        box.left = leftChildTittle;
        box.width = widthChildTitle;
        box.height = calculateHeight();
      };
      batchUpdates.push({ id: boxChild.id, fn });
    }
  });

  // Add the dragged box itself as the last update
  batchUpdates.push({
    id: boxId,
    fn: (box) => {
      box.type = titleId;
      box.clientWidth = width;
      box.clientHeight = calculateHeight();
      box.left = left;
      box.width = width;
      box.height = calculateHeight();
    },
  });

  // Single store write: one produce instead of N
  if (batchUpdateBox) {
    batchUpdateBox(batchUpdates);
  } else {
    // Fallback for callers that don't provide batchUpdateBox (e.g. Title.jsx)
    batchUpdates.forEach(({ id, fn }) => updateBox(id, fn));
  }

  updateIndexTitle();
};

// Returns draft.boxes sorted by visual document order (containers → columns → boxes)
const getBoxesInDocumentOrder = (draft) => {
  const boxById = new Map(draft.boxes.map((b) => [b.id, b]));
  const colById = new Map(draft.columns.map((c) => [c.id, c]));
  const ordered = [];
  const addedIds = new Set();
  for (const container of draft.containers) {
    if (!container.columnsIds) continue;
    for (const colId of container.columnsIds) {
      const col = colById.get(colId);
      if (!col) continue;
      for (const box of draft.boxes) {
        if (box.columnId === colId && !addedIds.has(box.id)) {
          ordered.push(box);
          addedIds.add(box.id);
        }
      }
    }
  }
  // Append any boxes not yet seen (e.g. orphaned boxes) so nothing is lost
  for (const box of draft.boxes) {
    if (!addedIds.has(box.id)) ordered.push(box);
  }
  return ordered;
};

export const mutateTitlesIndexes = (draft, maskId = null) => {
  // titlesTypeConfig is an array of each level type
  // ex: ['roman', '', 'letterMaj', 'letterMin']
  let indexCounter = [0, 0, 0, 0];
  // Compteurs de l'index affiché : les titres hors numérotation n'en consomment
  // pas, pour que le titre numéroté suivant prenne le numéro qu'attend le
  // lecteur (I, puis un titre sans numéro, puis II).
  let displayCounter = [0, 0, 0, 0];
  // Niveau du titre hors numérotation en cours : ses sous-titres le suivent,
  // jusqu'au prochain titre de niveau égal ou supérieur.
  let unnumberedFromLevel = null;
  let typeTitle = ['title1', 'title2', 'title3', 'title4'];
  const mask = draft.masks.find((m) => m.id === maskId);
  // When processing the full document, iterate in visual document order so that
  // boxes appended to the end of draft.boxes (e.g. linked title boxes) are
  // numbered according to where their container sits, not their array position.
  let boxesToUpdate = maskId === null ? getBoxesInDocumentOrder(draft) : mask.boxes;
  // Map: linkedToMaskId → { indexTitle, displayIndexTitle }, to propagate to chapter sep mask titles
  const linkedTitleIndexByMaskId = new Map();
  // return produce(boxes, (draftBoxes) => {
  boxesToUpdate = boxesToUpdate.map((box, index, boxes) => {
    if (!isATitleBox(box.type)) {
      return box;
    }
    if (maskId === null && isExcludedChapterTitle(box, draft.columns, draft.masks, draft.configuration)) {
      return box;
    }

    const boxLevel = getTitleLevelIndex(box.type);
    if (unnumberedFromLevel !== null && boxLevel <= unnumberedFromLevel) {
      unnumberedFromLevel = null;
    }
    const unnumbered = unnumberedFromLevel !== null || box.unnumbered === true;
    if (unnumbered && unnumberedFromLevel === null) {
      unnumberedFromLevel = boxLevel;
    }
    if (boxLevel + 1 < indexCounter.length) {
      const indexesToReset = _.range(boxLevel + 1, indexCounter.length);
      indexesToReset.forEach((indexValue) => {
        indexCounter[indexValue] = 0;
        displayCounter[indexValue] = 0;
      });
    }
    indexCounter[boxLevel] += 1;
    const indexTitle = indexCounter.slice(0, boxLevel + 1);
    if (!unnumbered) displayCounter[boxLevel] += 1;
    const displayIndexTitle = unnumbered
      ? []
      : displayCounter.slice(0, boxLevel + 1);
    // Record indexTitle for linked titles so we can propagate to the paired chapter sep mask title
    if (maskId === null && box.linkedToMaskId) {
      linkedTitleIndexByMaskId.set(box.linkedToMaskId, {
        indexTitle,
        displayIndexTitle,
      });
    }
    return {
      ...box,
      type: typeTitle[boxLevel],
      content: {
        ...box.content,
        indexTitle,
        displayIndexTitle,
      },
    };
  });

  // Propagate indexTitle to chapter sep mask title1 boxes only (injected in draft.boxes + in mask.boxes)
  // title2+ inside chapter sep masks are decorative and keep their own indexTitle
  if (maskId === null && linkedTitleIndexByMaskId.size > 0) {
    boxesToUpdate = boxesToUpdate.map((box) => {
      if (box.type !== ItemTypes.TITLE || box.linkedToMaskId) return box;
      const col = draft.columns.find((c) => c.id === box.columnId);
      if (!col?.maskId) return box;
      const idx = linkedTitleIndexByMaskId.get(col.maskId);
      if (idx === undefined) return box;
      return { ...box, content: { ...box.content, ...idx } };
    });
    for (const [chapterSepMaskId, idx] of linkedTitleIndexByMaskId) {
      const chapterSepMask = draft.masks.find((m) => m.id === chapterSepMaskId);
      if (chapterSepMask?.chapterSeparation) {
        chapterSepMask.boxes = chapterSepMask.boxes.map((b) => {
          if (b.type !== ItemTypes.TITLE) return b;
          return { ...b, content: { ...b.content, ...idx } };
        });
      }
    }
  }

  if (maskId === null) {
    draft.boxes = boxesToUpdate;
  } else {
    mask.boxes = boxesToUpdate;
  }
};

// export const resizeSummary = (nextBoxes, summaryConfig) => {
//   let totalHeight = 0;
//   nextBoxes.forEach((box) => {
//     if (
//       // If it's a title (type included in ["title1", "title2"...])
//       Object.values(TitlesTypes).includes(box.type) &&
//       // And it's visible
//       summaryConfig[box.type].visibility === 'visible'
//     ) {
//       totalHeight += box.height;
//     }
//   });

//   return nextBoxes.map((box) => {
//     if (box.type === ItemTypes.SUMMARY) {
//       return {
//         // same as previous box
//         ...box,
//         // BUT the height is new
//         height: totalHeight,
//       };
//     } else {
//       return box;
//     }
//   });
// };

export const mutateResizeSummary = (draft, maskId = null) => {
  const mask = draft.masks.find((m) => m.id === maskId);
  const boxesToUpdate = maskId === null ? draft.boxes : mask.boxes;
  const summaryConfig = draft.summaryConfiguration;
  let totalHeight = 0;
  boxesToUpdate.forEach((box) => {
    if (
      // If it's a title (type included in ["title1", "title2"...])
      Object.values(TitlesTypes).includes(box.type) &&
      // And it's visible
      summaryConfig[box.type].visibility === 'visible' &&
      // Exclude chapter sep mask titles and hidden linked titles
      !isExcludedChapterTitle(box, draft.columns, draft.masks, draft.configuration)
    ) {
      totalHeight += box.height;
    }
  });
  // ICI voir si pas de problème avec la fonctionnalité d'affichage
  // de certaines lignes seulement dans certains sommaires
  boxesToUpdate
    .filter((box) => box.type === ItemTypes.SUMMARY)
    .map((b) => {
      return {
        // same as previous box
        ...b,
        // BUT the height is new
        height: totalHeight,
      };
    });
};

export const indentTitles = ({ box, columns, margins, indentationTitles }) => {
  const column = columns.find((c) => c.id === box.columnId);

  if (!column || column.drawMode || !isATitleBox(box.type)) return box;

  return {
    ...box,
    width: calculateWidth(
      margins,
      indentationTitles,
      box.content.indexTitle.length - 1
    ),
  };
};
